import { useEffect, useState } from 'react';
import { posFitsSplit } from '../../utils/posView';
import { scrollParentOf } from './scrollParent';

/**
 * ¿Caben, lado a lado, el catálogo y el pedido?
 *
 * Lo decide el ancho de la PANTALLA DEL PUNTO DE VENTA —el elemento que se le pasa—, no el de la
 * ventana ni el equipo: el menú del shell cambia cuánto sitio queda, y un teléfono apaisado puede
 * tener más que una ventana angosta en una computadora. Donde no se puede medir, la respuesta es
 * «no»: el pedido va en la barra de abajo, que funciona en cualquier ancho.
 */
export function useFitsSplit(element: HTMLElement | null): boolean {
  const [fits, setFits] = useState(false);
  useEffect(() => {
    if (!element || typeof ResizeObserver === 'undefined') {
      setFits(false);
      return undefined;
    }
    const measure = () => setFits(posFitsSplit(element.getBoundingClientRect().width));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);
  return fits;
}

/** La separación entre el panel del pedido y el borde de lo que se desplaza (`--n3-space-12`). */
export const POS_PANEL_INSET = 12;
/** El alto que el panel conserva aunque arranque muy abajo en la página: sigue siendo usable. */
export const POS_PANEL_MIN_ROOM = 320;

/**
 * Cuánto alto le queda al panel del pedido, desde donde está hasta el borde de abajo de lo que se
 * desplaza.
 *
 * El panel va al lado del catálogo y se queda a la vista mientras el catálogo se desplaza. Para que
 * «Cobrar» —que va al pie del panel— nunca quede por debajo del borde, el panel no puede medir más
 * que ese hueco: con el pedido largo, lo que se desplaza son sus líneas. El hueco cambia al
 * desplazar la página (el panel sube hasta pegarse arriba) y al cambiar el alto de lo que tiene
 * encima, así que se vuelve a medir en los dos casos.
 *
 * Abajo se descuenta lo que hay DESPUÉS de la pantalla dentro de lo que se desplaza (el relleno del
 * contenido): al llegar al final, el panel no puede bajar más que la pantalla que lo contiene, y si
 * midiera más que el hueco hasta ahí, se subiría y su título quedaría cortado. Ese espacio no depende
 * del alto del panel, así que medirlo no lo hace crecer ni encoger en vueltas.
 *
 * `watched` es la pantalla del punto de venta (la sección que contiene al panel). Donde no se puede
 * medir devuelve `undefined`, y vale lo que supone la hoja de estilos.
 */
export function useRoomBelow(element: HTMLElement | null, watched: HTMLElement | null): number | undefined {
  const [room, setRoom] = useState<number>();
  useEffect(() => {
    const scroller = element ? scrollParentOf(element) : null;
    if (!element || !scroller || typeof ResizeObserver === 'undefined') {
      setRoom(undefined);
      return undefined;
    }
    let frame = 0;
    const measure = () => {
      frame = 0;
      // Solo se mira dónde EMPIEZA el panel, no cuánto mide: su alto depende de este mismo número.
      const area = scroller.getBoundingClientRect();
      const top = Math.max(element.getBoundingClientRect().top - area.top, POS_PANEL_INSET);
      const after = watched ? scroller.scrollHeight - (watched.getBoundingClientRect().bottom - area.top + scroller.scrollTop) : 0;
      const next = Math.max(Math.floor(scroller.clientHeight - top - Math.max(POS_PANEL_INSET, after)), POS_PANEL_MIN_ROOM);
      setRoom((current) => (current === next ? current : next));
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(measure); };
    measure();
    scroller.addEventListener('scroll', schedule, { passive: true });
    const observer = new ResizeObserver(schedule);
    observer.observe(scroller);
    if (watched) observer.observe(watched);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      scroller.removeEventListener('scroll', schedule);
      observer.disconnect();
    };
  }, [element, watched]);
  return room;
}
